import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Card, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TextField, Tooltip, Typography, CircularProgress
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';

const emptyEdit = {
  company_name: '',
  dba_name: '',
  contract_number: '',
  website: '',
  phone: '',
  email: '',
  address_line1: '',
  city: '',
  state: '',
  zip_code: '',
  sam_uei: '',
  notes: '',
};

export default function GsaCompanies() {
  const { hasPermission } = useAuth();
  const canModify = hasPermission('extracted_data_modify');
  const canDelete = hasPermission('extracted_data_delete');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [viewRow, setViewRow] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState(emptyEdit);
  const [deleteId, setDeleteId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        ...(search && { search }),
      });
      const r = await api.get(`/extracted-data/gsa-companies?${params}`);
      setRows(r.data?.data || []);
      setTotal(r.data?.pagination?.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load GSA companies');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search]);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = (row) => {
    setEditForm({
      company_name: row.company_name || '',
      dba_name: row.dba_name || '',
      contract_number: row.contract_number || '',
      website: row.website || '',
      phone: row.phone || '',
      email: row.email || '',
      address_line1: row.address_line1 || '',
      city: row.city || '',
      state: row.state || '',
      zip_code: row.zip_code || '',
      sam_uei: row.sam_uei || '',
      notes: row.notes || '',
    });
    setEditRow(row);
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.put(`/extracted-data/gsa-companies/${editRow.id}`, editForm);
      toast.success('Updated');
      setEditRow(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/extracted-data/gsa-companies/${deleteId}`);
      toast.success('Deleted');
      setDeleteId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>GSA Companies</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Search name / contract…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          />
          <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
        </Box>
      </Box>

      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                    <TableCell>Company</TableCell>
                <TableCell>DBA</TableCell>
                <TableCell>Contract #</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Letter</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    No GSA companies yet. Pull from Settings → Extract Data from Libraries → GSA Library.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.company_name}</TableCell>
                    <TableCell>{row.dba_name || '—'}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{row.contract_number || '—'}</TableCell>
                    <TableCell>{row.email || '—'}</TableCell>
                    <TableCell>{row.phone || '—'}</TableCell>
                    <TableCell>{row.letter_index || '—'}</TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="View">
                        <IconButton size="small" onClick={() => setViewRow(row)}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={canModify ? 'Edit' : 'No edit permission (extracted_data_modify)'}>
                        <span>
                          <IconButton size="small" disabled={!canModify} onClick={() => openEdit(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <span>
                          <IconButton size="small" color="error" disabled={!canDelete} onClick={() => setDeleteId(row.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Card>

      <Dialog open={Boolean(viewRow)} onClose={() => setViewRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>GSA Company</DialogTitle>
        <DialogContent dividers>
          {viewRow && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2"><strong>Company:</strong> {viewRow.company_name}</Typography>
              <Typography variant="body2"><strong>DBA:</strong> {viewRow.dba_name || '—'}</Typography>
              <Typography variant="body2"><strong>Contract:</strong> {viewRow.contract_number || '—'}</Typography>
              <Typography variant="body2"><strong>Phone:</strong> {viewRow.phone || '—'}</Typography>
              <Typography variant="body2"><strong>Email:</strong> {viewRow.email || '—'}</Typography>
              <Typography variant="body2"><strong>Website:</strong> {viewRow.website || '—'}</Typography>
              <Typography variant="body2"><strong>SAM UEI:</strong> {viewRow.sam_uei || '—'}</Typography>
              <Typography variant="body2"><strong>Address:</strong> {[viewRow.address_line1, viewRow.city, viewRow.state, viewRow.zip_code].filter(Boolean).join(', ') || '—'}</Typography>
              {viewRow.detail_url && (
                <Typography variant="body2">
                  <strong>GSA detail:</strong>{' '}
                  <a href={viewRow.detail_url} target="_blank" rel="noreferrer">Open</a>
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewRow(null)}>Close</Button>
          {canModify && viewRow && (
            <Button
              variant="contained"
              onClick={() => {
                openEdit(viewRow);
                setViewRow(null);
              }}
            >
              Edit
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editRow)} onClose={() => setEditRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit GSA Company</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {Object.keys(emptyEdit).map((k) => (
              <TextField
                key={k}
                size="small"
                label={k.replace(/_/g, ' ')}
                value={editForm[k] || ''}
                onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })}
                multiline={k === 'notes'}
                minRows={k === 'notes' ? 2 : undefined}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRow(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveEdit} disabled={busy}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteId)} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete company?</DialogTitle>
        <DialogContent>This cannot be undone.</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={busy}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
